// The Outlook's four posture bands (All clear · Minor issues · Needs attention ·
// Critical) and the severity rule behind them, in ONE place. The Outlook lights
// a band from the worst active severity; clicking a band opens the incidents
// feed filtered by the same rule. Two copies of the thresholds would let the
// band a reader clicks and the incidents it opens disagree.

import { SEVERITY, type SeverityKey } from './severity'

export type PostureBand = 'clear' | 'minor' | 'attention' | 'critical'
/** A band that holds open problems — every band but "All clear". */
export type ProblemBand = Exclude<PostureBand, 'clear'>

/** Left-to-right strip order. */
export const POSTURE_BANDS: readonly PostureBand[] = ['clear', 'minor', 'attention', 'critical']

export const POSTURE_BAND_LABEL: Record<PostureBand, string> = {
  clear: 'All clear',
  minor: 'Minor issues',
  attention: 'Needs attention',
  critical: 'Critical',
}

/** Raw severities per problem band, lowest first. */
export const BAND_SEVERITIES: Record<ProblemBand, readonly [Exclude<SeverityKey, 'ok'>, Exclude<SeverityKey, 'ok'>]> = {
  minor: [0, 1],
  attention: [2, 3],
  critical: [4, 5],
}

/** Raw Zabbix severity → band: High/Disaster → critical, Warning/Average →
 *  attention, Not classified/Information → minor. */
export function bandOfSeverity(sev: number): ProblemBand {
  if (sev >= 4) return 'critical'
  if (sev >= 2) return 'attention'
  return 'minor'
}

/** "High or Disaster" — the band's severities in words, lowest first, from
 *  the canonical registry. */
export function bandSeverityWords(band: ProblemBand): string {
  const [lo, hi] = BAND_SEVERITIES[band]
  return `${SEVERITY[lo].label} or ${SEVERITY[hi].label}`
}

/** A URL value (`?severity=`) → the problem band it names; null for anything
 *  else, so a mistyped link shows the whole feed rather than an empty one. */
export function parseProblemBand(v: string | null | undefined): ProblemBand | null {
  return v === 'minor' || v === 'attention' || v === 'critical' ? v : null
}

/** The dashboard incidents page for a clicked band: a problem band filters the
 *  Live feed to its severities, "All clear" opens the Resolved tab. Both hosts
 *  link here — the dashboard by router, the widget through the app hand-off —
 *  so the URL is built once; the dashboard's IncidentsPage parses it back. */
export function bandIncidentsPath(band: PostureBand): string {
  return band === 'clear' ? '/incidents?tab=resolved' : `/incidents?severity=${band}`
}
